import { useEffect, useState } from 'react';
import usePersonTestItemInputData from 'hooks/queries/usePersonTestItemInputData';
import {
  offset,
  useFloating,
  autoUpdate,
  flip,
  hide
} from '@floating-ui/react';
import '../../../../components/tooltip/Tooltip.scss';
import classNames from 'classnames';
import Loader from 'components/loader/Loader';
import { sort, SORT_DATA_TYPES } from 'utils/sort';
import TableRow from 'components/table/TableRow';
import TableBody from 'components/table/TableBody';
import TableHeader from 'components/table/TableHeader';
import TableHeaderCell from 'components/table/TableHeaderCell';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import Button from 'components/button/Button';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import { formatValue } from 'utils/results';

const InputDataSelector = ({
  personId,
  testId,
  testItem,
  testItemId,
  laterality,
  onChange,
  rootContext,
  setTooltip,
  ...props
}) => {
  const { fetchInputData, personInputData, loading } =
    usePersonTestItemInputData();

  const { isPositioned, floatingStyles, middlewareData } = useFloating({
    rootContext,
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end',
    middleware: [
      offset({
        mainAxis: 8,
        crossAxis: 0
      }),
      flip(),
      hide()
    ]
  });
  const [filteredInputData, setFilteredInputData] = useState([]);

  useEffect(() => {
    if (isPositioned && personId && testId) {
      fetchInputData({ personId, testItemId, testId });
    }
  }, [isPositioned, personId, testId]);

  useEffect(() => {
    if (personInputData?.length > 0) {
      if (laterality) {
        const inputData = personInputData.filter(
          dataPoint => dataPoint.type === laterality
        );

        setFilteredInputData(
          sort(inputData, {
            keys: [
              {
                key: 'testDateTime',
                desc: true,
                dataType: SORT_DATA_TYPES.DATE_STRING
              }
            ]
          })
        );
      } else {
        setFilteredInputData(personInputData);
      }
    } else {
      setFilteredInputData([]);
    }
  }, [personInputData, testItemId]);

  const onChangeHandler = dataPoint => {
    onChange(dataPoint);
  };

  return (
    <div
      ref={setTooltip}
      className={classNames('c-tooltip__message c-tooltip-popper__message')}
      style={{
        ...floatingStyles,
        visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
        maxHeight: 170,
        minHeight: 64,
        width: 256,
        display: 'flex',
        alignItems: 'flex-start',
        padding:
          !loading && !(filteredInputData && filteredInputData.length > 0)
            ? 24
            : 0,
        overflow: loading ? 'hidden' : 'auto'
      }}
      {...props}
    >
      <>
        {loading && <Loader />}
        {filteredInputData && filteredInputData.length > 0 ? (
          <Table fixed>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>
                  <FormattedMessage {...messages.inputDataTableTimestamp} />
                </TableHeaderCell>
                <TableHeaderCell>
                  {testItem.unit ? (
                    testItem.unit
                  ) : (
                    <FormattedMessage {...messages.inputDataTableValues} />
                  )}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInputData.map(dataPoint => (
                <TableRow
                  key={dataPoint.id}
                  onClick={() => onChangeHandler(dataPoint)}
                >
                  <TableCell>
                    <div style={{ color: 'var(--color-neutral-dark)' }}>
                      {new Date(dataPoint.testDateTime).toLocaleDateString([], {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}{' '}
                      —{' '}
                      {new Date(dataPoint.testDateTime).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      small
                      ghost
                      primary
                      style={{ margin: 'auto', padding: '2px 4px' }}
                      onClick={() => onChangeHandler(dataPoint)}
                    >
                      {formatValue(dataPoint.values, 4)}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          !loading && (
            <div style={{ textAlign: 'center', width: '100%' }}>
              No input data available.
            </div>
          )
        )}
      </>
    </div>
  );
};

export default InputDataSelector;
